import * as React from 'react';
import { getWhiteLabelLogo, isWhiteLabel } from '@/redux/modules/whiteLabel';
import { isUserDataLoading, isUserLoggedIn } from '@/redux/modules/user';
import styled, { css } from 'styled-components';
import Crown from './Crown';
import DiscoveryTags from '@/components/DiscoveryTags/DiscoveryTags';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import LoggedInMenu from './Login/LoggedInMenu';
import LoggedOut from './Login/LoggedOut';
import MobileNavigation from './Navigation/MobileNavigation';
import Navigation from './Navigation/Navigation';
import { parseQueryParams } from '../../utils/queryParams';
import SearchInput from '@/components/SearchInput/SearchInput';
import useIsMobile from '@/hooks/useIsMobile';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

const Header = () => {
    const { pathname, search } = useLocation();
    const logo = useSelector(getWhiteLabelLogo, isEqual);
    const userDataLoading = useSelector(isUserDataLoading, isEqual);
    const userLoggedIn = useSelector(isUserLoggedIn, isEqual);
    const whiteLabel = useSelector(isWhiteLabel, isEqual);
    const isMobile = useIsMobile();

    const queryParams: any = parseQueryParams(search);
    const searchBarDesktop = 'search-bar-desktop';
    const searchBarMobile = 'search-bar-mobile';

    return (
        <StyledHeader>
            {!isMobile && <Crown />}
            <StyledWrapper whiteLabel={whiteLabel}>
                <StyledSearchBar loggedIn={userLoggedIn}>
                    <StyledImageContainer>
                        {!whiteLabel && isMobile && <MobileNavigation />}
                        <StyledHeaderLogLink route to="/">
                            {Boolean(logo) && <StyledHeaderLogo alt="logo" src={logo} />}
                        </StyledHeaderLogLink>
                    </StyledImageContainer>
                    <StyledSearchFieldContainer>
                        {/* special element for Jasper52 */}
                        <StyledExceptionalItems id="exceptional-items">
                            <FormattedMessage id="header.j52" />
                        </StyledExceptionalItems>
                        {!isMobile && (
                            <SearchInput
                                id="desktop-search"
                                name={searchBarDesktop}
                                pathname={pathname}
                                queryParams={queryParams}
                            />
                        )}
                    </StyledSearchFieldContainer>
                    <StyledControls>
                        {userLoggedIn || userDataLoading ? <LoggedInMenu parent={this} /> : <LoggedOut />}
                    </StyledControls>
                </StyledSearchBar>
                {isMobile && (
                    <StyledMobileSearch>
                        <SearchInput
                            id="mobile-search"
                            name={searchBarMobile}
                            pathname={pathname}
                            queryParams={queryParams}
                            reverseButton
                        />
                    </StyledMobileSearch>
                )}
            </StyledWrapper>
            {!whiteLabel && !isMobile && <Navigation />}
            {!whiteLabel && isMobile && <StyledTags isInHeader key="discoveryTags" width={1024} />}
        </StyledHeader>
    );
};

export default React.memo(Header);

const StyledHeader = styled.div`
    @media print {
        display: none;
    }

    div[class='popover-content'] {
        padding: 10px 0 0;
    }

    @media (max-width: 767px) {
        margin-top: 5px;
    }
`;

const StyledControls = styled.div`
    height: 44px;
    flex: 0 0 auto;
    margin-left: 0;
    display: flex;
`;

interface WrapperProps {
    whiteLabel: boolean;
}

const StyledWrapper = styled.div<WrapperProps>`
    position: relative;
    z-index: 13;
    ${({ whiteLabel }) =>
        whiteLabel
            ? css`
                  max-width: 1240px;
                  margin: 0 auto 55px;
              `
            : ''};
`;
interface SearchBarProps {
    loggedIn: boolean;
}

const StyledSearchBar = styled.div<SearchBarProps>`
    display: flex;
    padding: 13px 20px;

    @media (min-width: 1240px) {
        margin: 0 auto;
        max-width: 1240px;
    }

    @media (max-width: 767px) {
        padding: 2px 20px 0;
        ${({ loggedIn }) =>
            loggedIn
                ? css`
                      padding: 2px 8px 0 20px;
                  `
                : ''};
    }
`;

const StyledImageContainer = styled.div`
    flex: 0 1 181px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: -15px 10px -15px 0;
    position: relative;

    @media (max-width: 991px) {
        flex: 0 1 123px;
    }

    @media (max-width: 767px) {
        flex: 1 1 auto;
    }

    & > div {
        padding: 0 20px 0 0;
        font-size: 20px;
    }
`;

const StyledHeaderLogLink = styled(Link)`
    height: 74px;
    width: 181px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    @media (max-width: 991px) {
        max-height: 64px;
        max-width: 123px;
    }
`;

const StyledHeaderLogo = styled.img`
    max-height: 74px;
    max-width: 181px;
    display: block;

    @media (max-width: 767px) {
        padding-top: 0;
    }

    @media (max-width: 991px) {
        max-height: 64px;
        max-width: 121px;
    }
`;

const StyledSearchFieldContainer = styled.div`
    height: 44px;
    margin: 0 12px 0 15px;
    flex: 1 1 auto;
    position: relative;
    display: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: block;
        margin: 0 12px 0 5px;
    }

    @media (min-width: 769px) {
        margin: 0 6px 0 26px;
    }
`;

const StyledExceptionalItems = styled.div`
    font-size: 15px !important;
    color: ${({ theme }) => theme.colors.red100};
    font-family: ${({ theme }) => theme.typography.fontPrimary} !important;
    font-style: italic;
    flex: 1 1 500px;
    display: none;
    align-items: center;
    margin-right: 20px;
`;

const StyledMobileSearch = styled.div`
    transition: border-bottom 0.3s, overflow 0.1s ease 0.5s, padding 0.3s, height 0.3s;
    padding: 6px 20px 10px;
    height: 55px !important;
    overflow: inherit;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const StyledTags = styled(DiscoveryTags)`
    && {
        position: relative;
        z-index: 1;
        padding: 0;
        overflow: hidden;
        margin: 0 auto;
        background-color: white;

        div:first-child {
            margin: 0;
            width: 100%;
            display: inline-block;
            white-space: nowrap;
            overflow: -moz-scrollbars-none;
            -ms-overflow-style: none;
            overflow-x: auto;
            overflow-y: hidden;
            height: 55px;
            > * {
                color: ${({ theme }) => theme.colors.grey100};
                background-color: transparent;
                border-radius: 2px;
                height: 36px;
                display: inline-flex;
                padding: 0 8px;
                align-items: center;
                font-family: ${({ theme }) => theme.typography.fontSecondary};
                font-size: 14px;
                letter-spacing: 0.5px;
                text-decoration: none;
                box-shadow: none;
                margin-right: 0;

                &:hover {
                    background-color: transparent;
                    color: ${({ theme }) => theme.colors.grey100};
                }
            }
        }

        .tags::-webkit-scrollbar {
            display: none !important;
        }
    }
`;
